import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULTS,
  requestListSchema,
  type RequestListState,
} from "./requestListSchema";

// Array-valued URL params: multi-value via repeated keys (?client=A&client=B).
const ARRAY_KEYS = new Set([
  "requestType",
  "status",
  "calculatedStatus",
  "client",
  "lob",
  "eng",
  "assigned",
  "team",
  "division",
  "manager",
  "country",
  "bucket",
  "groupBy",
  "drillKeys",
  "drillVals",
  "qPills",
]);
const NUMBER_KEYS = new Set(["dateRange", "page", "pageSize", "minSample"]);
const BOOLEAN_KEYS = new Set(["rework", "onboardingsOnly", "redeployment"]);

function parseFromSearchParams(params: URLSearchParams): RequestListState {
  const raw: Record<string, unknown> = {};
  // First pass: collect arrays.
  for (const key of ARRAY_KEYS) {
    const values = params.getAll(key);
    if (values.length > 0) raw[key] = values;
  }
  // Second pass: scalars.
  for (const [key, value] of params.entries()) {
    if (ARRAY_KEYS.has(key)) continue; // already handled
    if (NUMBER_KEYS.has(key)) {
      const n = Number(value);
      if (!Number.isNaN(n)) raw[key] = n;
    } else if (BOOLEAN_KEYS.has(key)) {
      raw[key] = value === "true";
    } else {
      raw[key] = value;
    }
  }
  const result = requestListSchema.safeParse(raw);
  if (result.success) return result.data;
  // Drop invalid values but keep parseable ones by re-parsing with the invalid keys removed.
  if (typeof console !== "undefined") {
    console.warn("[requestList] invalid URL state, dropping bad fields:", result.error.issues);
  }
  const issuePaths = new Set(result.error.issues.map((i) => i.path[0] as string));
  for (const path of issuePaths) delete raw[path];
  return requestListSchema.parse(raw);
}

function serializeToSearchParams(state: RequestListState): URLSearchParams {
  const params = new URLSearchParams();
  const def = DEFAULTS;
  for (const [key, value] of Object.entries(state) as [keyof RequestListState, unknown][]) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue; // default empty array
      for (const v of value) params.append(key, String(v));
      continue;
    }
    if (typeof value === "boolean") {
      if (value === (def as Record<string, unknown>)[key]) continue;
      params.set(key, value ? "true" : "false");
      continue;
    }
    // Skip defaults: keeps shared URLs clean.
    if ((def as Record<string, unknown>)[key] === value) continue;
    if (value === "") continue;
    params.set(key, String(value));
  }
  return params;
}

export function useUrlSearchState(): [RequestListState, (next: Partial<RequestListState>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseFromSearchParams(searchParams), [searchParams]);

  const set = useCallback(
    (next: Partial<RequestListState>) => {
      // Merge with current parsed state to produce a complete next state, then serialize.
      // We re-parse the merged shape to enforce schema validity on the way out.
      const merged = requestListSchema.parse({ ...state, ...next });
      setSearchParams(serializeToSearchParams(merged), { replace: false });
    },
    [state, setSearchParams],
  );

  return [state, set];
}
